import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { useStores } from '@components/common/root-store-provider/root-store-provider';
import {
  GROUPED_VARIANTS,
  type TpSource,
  type TpVariantObject,
} from 'src/constants/variants';
import {
  APPLOVIN_PIXEL_ID,
  BING_ID,
  CLARITY_ID,
  FACEBOOK_PIXEL_ID,
  GA_ID,
  GA_ID_V2,
  PINTEREST_ID,
  SNAPCHAT_PIXEL_ID,
  TIK_TOK_PIXEL,
  TWITTER_ID,
} from '../constants';

export const useShouldInit = () => {
  const {
    authStore: { variant },
  } = useStores();

  const router = useRouter();
  const { isReady, query } = router;

  const isValidVariantSource = useCallback(
    (source: TpSource) =>
      GROUPED_VARIANTS.bySource
        .get(source)
        ?.some((item: TpVariantObject) => item.variant_name === variant),
    [variant],
  );

  const TIK_TOK_SOURCES = isValidVariantSource('tiktok');
  const TWITTER_SOURCES = isValidVariantSource('twitter');
  const PINTEREST_SOURCES = isValidVariantSource('pinterest');
  const GOOGLE_SOURCES = isValidVariantSource('google');
  const FACEBOOK_SOURCES = isValidVariantSource('facebook');
  const BING_SOURCES = isValidVariantSource('bing');
  const SNAPCHAT_SOURCES = isValidVariantSource('snapchat');
  const APPLOVIN_SOURCES = true;

  const shouldPixelInit =
    FACEBOOK_PIXEL_ID &&
    isReady &&
    FACEBOOK_SOURCES &&
    query['reset'] === undefined;
  const shouldPTInit =
    PINTEREST_ID &&
    PINTEREST_SOURCES &&
    isReady &&
    query['reset'] === undefined;
  const shouldBingInit =
    BING_ID && BING_SOURCES && isReady && query['reset'] === undefined;
  const shouldTwitterInit =
    TWITTER_ID && TWITTER_SOURCES && isReady && query['reset'] === undefined;
  const shouldTikTokInit =
    TIK_TOK_PIXEL && TIK_TOK_SOURCES && isReady && query['reset'] === undefined;
  const shouldSnapchatInit =
    SNAPCHAT_PIXEL_ID &&
    SNAPCHAT_SOURCES &&
    isReady &&
    query['reset'] === undefined;
  const shouldGoogleAnalyticsInit = GA_ID || GA_ID_V2;
  const shouldAppLovinInit = Boolean(APPLOVIN_PIXEL_ID) && APPLOVIN_SOURCES;

  const isSpecificHostname =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'quiz.mimika-app.com' ||
      window.location.hostname === 'quiz.mimika-uni.com');

  const shouldGoogleAnalyticsV2Init = isSpecificHostname;
  const shouldClarityInit = CLARITY_ID;
  const shouldClarityV2Init = isSpecificHostname;

  return {
    shouldPixelInit,
    shouldPTInit,
    shouldTwitterInit,
    shouldTikTokInit,
    shouldGoogleAnalyticsInit,
    shouldGoogleAnalyticsV2Init,
    shouldAppLovinInit,
    shouldClarityInit,
    shouldClarityV2Init,
    shouldBingInit,
    shouldSnapchatInit,
  };
};
